import { adminPost } from "@/lib/kbAdminClient";
import type { GapInboxItem, PanelNote, RelatedArticlePreview } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  gapInboxTestingOnly: boolean;
  gapInboxPage: number;
  gapInboxPageSize: 5 | 10;
  setGapInboxLoading: (v: boolean) => void;
  setGapInboxOpenCount: (v: number) => void;
  setGapInboxListTotal: (v: number) => void;
  setGapInboxItems: (v: GapInboxItem[]) => void;
  setRelatedPreviewLoading: (v: boolean) => void;
  setRelatedPreview: (v: RelatedArticlePreview | null) => void;
  showNote: ShowNote;
  showAiFeedback: ShowNote;
};

export function useKbGapInboxData(p: Params) {
  async function previewRelatedArticle(slug: string, label: string) {
    p.setRelatedPreviewLoading(true);
    try {
      const response = await adminPost<{ ok: true; article: any }>("/api/kb/admin/getArticles", { slug, includeChunks: false });
      if (!response.ok) return p.showAiFeedback({ tone: "error", title: "Related article preview failed", text: response.error });
      const article = response.data.article;
      p.setRelatedPreview({
        slug: article.slug || slug,
        title: article.title || label,
        body_markdown: article.body_markdown || "",
        scope: article.scope || null,
        doc_type: article.doc_type || null,
        status: article.status || "draft",
        last_edited_by: article.last_edited_by || null,
        updated_at: article.updated_at || null,
      });
    } finally {
      p.setRelatedPreviewLoading(false);
    }
  }

  async function refreshGapInboxCount() {
    const response = await adminPost<{ ok: true; openCount: number }>("/api/kb/admin/getGaps", { countOnly: true });
    if (response.ok) p.setGapInboxOpenCount(Number(response.data.openCount || 0));
  }

  async function refreshGapInbox(statusFilter?: string, testingOnlyArg?: boolean, pageOverride?: number, pageSizeOverride?: number) {
    p.setGapInboxLoading(true);
    try {
      const testingOnly = testingOnlyArg ?? p.gapInboxTestingOnly;
      const page = pageOverride ?? p.gapInboxPage;
      const pageSize = pageSizeOverride ?? p.gapInboxPageSize;
      const offset = page * pageSize;
      const response = await adminPost<{ ok: true; openCount: number; listTotal?: number; items: GapInboxItem[]; offset?: number; limit?: number }>(
        "/api/kb/admin/getGaps",
        { status: statusFilter || undefined, testingOnly: testingOnly === true ? true : undefined, limit: pageSize, offset }
      );
      if (response.ok) {
        p.setGapInboxOpenCount(Number(response.data.openCount || 0));
        p.setGapInboxListTotal(Number(response.data.listTotal ?? response.data.items?.length ?? 0));
        p.setGapInboxItems(response.data.items || []);
      } else {
        p.showNote({ tone: "error", title: "Gap inbox load failed", text: response.error });
      }
    } finally {
      p.setGapInboxLoading(false);
    }
  }

  return { previewRelatedArticle, refreshGapInboxCount, refreshGapInbox };
}
